'use client';

import { useState, useCallback, useRef, useLayoutEffect } from "react";
import Image, { ImageProps } from "next/image";

type StrapiImageClientProps = ImageProps & {
  blurDataURL?: string;
  isSvg?: boolean;
};

const MAX_RETRIES = 2;

export function StrapiImageClient({ blurDataURL, isSvg, ...props }: StrapiImageClientProps) {
  const [loaded, setLoaded] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [gaveUp, setGaveUp] = useState(false);
  const [instant, setInstant] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Runs before the browser paints. If the browser already has this image
  // decoded (memory/disk cache - e.g. revisiting a page), the <img> is
  // `.complete` the instant it's mounted, so skip the blur flash + fade
  // entirely instead of animating from a state that was never really true.
  useLayoutEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setInstant(true);
      setShowImage(true);
      setLoaded(true);
    }
    // Only meaningful on mount / retry-remount, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount]);

  // Determine container sizing - if fill or className present, use flexible sizing
  const useFlexibleSize = props.fill || props.className?.includes('w-') || props.className?.includes('h-');

  const handleLoad = () => {
    setLoaded(true);
    // Trigger fade-in transition immediately
    setShowImage(true);
  };

  // Some image loads intermittently fail (e.g. a momentary hiccup from the
  // image optimizer under load) rather than being genuinely missing - retry a
  // couple of times with a short backoff before giving up. Bumping retryCount
  // changes the <Image>'s key below, forcing a real remount (a re-render alone
  // wouldn't make the browser re-attempt a failed src). On final failure, never
  // show the browser's native "broken image" icon: just render nothing.
  const handleError = useCallback(() => {
    setRetryCount((n) => {
      if (n >= MAX_RETRIES) {
        setGaveUp(true);
        return n;
      }
      const next = n + 1;
      setTimeout(() => setRetryCount(next), 600 * next);
      return n;
    });
  }, []);

  if (gaveUp) {
    // Keep the container so layout doesn't jump, but show nothing rather than
    // a broken-image glyph.
    return (
      <div
        aria-hidden="true"
        style={{
          display: useFlexibleSize ? 'block' : 'inline-block',
          width: useFlexibleSize ? '100%' : (props.width ? `${props.width}px` : 'auto'),
          height: useFlexibleSize ? '100%' : (props.height ? `${props.height}px` : 'auto'),
          backgroundColor: blurDataURL ? undefined : 'transparent',
        }}
      />
    );
  }


  return (
    <div className="relative" style={{ 
      display: useFlexibleSize ? 'block' : 'inline-block',
      width: useFlexibleSize ? '100%' : (props.width ? `${props.width}px` : 'auto'),
      height: useFlexibleSize ? '100%' : (props.height ? `${props.height}px` : 'auto'),
      overflow: 'hidden'
    }}>
      {/* PNG preview for SVGs (sharp, no blur) or blur placeholder for raster images */}
      {blurDataURL && (
        <div
          style={{
            position: 'absolute',
            top: isSvg ? '0' : '-2px',
            left: isSvg ? '0' : '-2px',
            right: isSvg ? '0' : '-2px',
            bottom: isSvg ? '0' : '-2px',
            backgroundImage: `url("${blurDataURL}")`,
            backgroundSize: props.style?.objectFit === 'contain' ? 'contain' : 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: isSvg ? 'none' : 'blur(4px)',
            opacity: showImage ? 0 : 1,
            transition: instant ? 'none' : 'opacity 0.4s',
          }}
          aria-hidden="true"
        />
      )}
      <Image
        key={retryCount}
        ref={imgRef}
        {...props}
        className={`relative z-10 ${props.className || ''}`}
        style={{
          ...props.style,
          opacity: showImage ? 1 : 0,
          transition: instant ? 'none' : 'opacity 0.4s',
        }}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
